/**
 * A ZIP writer, for bulk download.
 *
 * The app has no dependencies, and this is the smallest amount of ZIP that
 * makes a file every desktop can open. Entries are **stored**, not deflated:
 * the payloads here are PNGs, which are already compressed, so deflating them
 * would cost CPU to save nothing. That removes the need for a DEFLATE
 * implementation entirely, and what is left is CRC-32 plus three fixed record
 * types.
 *
 * The result is a `Blob` built from parts, not one `Uint8Array`. A session's
 * shots can be hundreds of megabytes, and assembling that in a growing array
 * would need the whole archive in memory twice over. This way the payload
 * `Blob`s are referenced, and the only large temporary is the one entry being
 * checksummed.
 *
 * ZIP64 is not implemented. It is only needed past 4 GB of output, and the
 * store's quota is far below that.
 *
 * @module web/js/zip
 */

/** One file in the archive. `name` is the path inside the ZIP. */
export interface ZipSource {
  name: string;
  data: Blob;
}

/** The ZIP record signatures, little-endian on disk. */
const LOCAL_SIG = 0x04034b50;
const CENTRAL_SIG = 0x02014b50;
const END_SIG = 0x06054b50;

/** Sets the "names are UTF-8" flag, so accents survive. */
const FLAG_UTF8 = 0x0800;

/** Store method: no compression. */
const METHOD_STORE = 0;

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

/** CRC-32 of a byte range, as ZIP defines it. */
export function crc32(data: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < data.length; i++) {
    c = CRC_TABLE[(c ^ data[i]!) & 0xff]! ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

/**
 * DOS date and time, to ZIP's own 1980 epoch and two-second resolution.
 *
 * The values are clamped rather than thrown on: a file dated before 1980 or
 * after 2107 still deserves to be in the archive.
 */
function dosDateTime(when: Date): { time: number; date: number } {
  const year = Math.max(0, Math.min(127, when.getFullYear() - 1980));
  const month = when.getMonth() + 1;
  const day = when.getDate();
  const hours = when.getHours();
  const minutes = when.getMinutes();
  const seconds = Math.floor(when.getSeconds() / 2);
  return {
    time: (hours << 11) | (minutes << 5) | seconds,
    date: (year << 9) | (month << 5) | day,
  };
}

/** A little-endian record builder: allocate, then write in format order. */
function record(length: number, write: (w: DataView) => void): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(length);
  write(new DataView(bytes.buffer));
  return bytes;
}

/**
 * Build a ZIP archive. `when` stamps every entry; the default is now.
 *
 * Every payload is read once, for its checksum. The archive then refers to the
 * original blobs, so peak memory is one payload rather than all of them.
 */
export async function zipBlob(entries: ZipSource[], when: Date = new Date()): Promise<Blob> {
  const encoder = new TextEncoder();
  const { time, date } = dosDateTime(when);

  if (entries.length > 0xffff) {
    throw new Error(`too many entries for a ZIP without ZIP64: ${entries.length}`);
  }

  const names = entries.map((entry) => {
    const name = encoder.encode(entry.name);
    if (name.length > 0xffff) throw new Error(`entry name too long: ${entry.name}`);
    return name;
  });

  const crcs = new Uint32Array(entries.length);
  for (let i = 0; i < entries.length; i++) {
    const bytes = new Uint8Array(await entries[i]!.data.arrayBuffer());
    crcs[i] = crc32(bytes);
  }

  // The fixed records are 30 and 46 bytes; the name is a separate blob part.
  const localSizes = entries.map((entry, i) => 30 + names[i]!.length + entry.data.size);
  const localTotal = localSizes.reduce((a, b) => a + b, 0);
  const centralSize = entries.reduce((sum, _entry, i) => sum + 46 + names[i]!.length, 0);

  const parts: BlobPart[] = [];
  const offsets: number[] = [];
  let offset = 0;

  for (let i = 0; i < entries.length; i++) {
    const size = entries[i]!.data.size;
    offsets.push(offset);
    offset += localSizes[i]!;
    parts.push(
      record(30, (w) => {
        w.setUint32(0, LOCAL_SIG, true);
        w.setUint16(4, 20, true); // version needed: 2.0
        w.setUint16(6, FLAG_UTF8, true);
        w.setUint16(8, METHOD_STORE, true);
        w.setUint16(10, time, true);
        w.setUint16(12, date, true);
        w.setUint32(14, crcs[i]!, true);
        w.setUint32(18, size, true); // compressed size, equal because stored
        w.setUint32(22, size, true); // uncompressed size
        w.setUint16(26, names[i]!.length, true);
        w.setUint16(28, 0, true); // no extra field
      }),
      names[i]!,
      entries[i]!.data,
    );
  }

  for (let i = 0; i < entries.length; i++) {
    const size = entries[i]!.data.size;
    parts.push(
      record(46, (w) => {
        w.setUint32(0, CENTRAL_SIG, true);
        w.setUint16(4, 20, true); // version made by
        w.setUint16(6, 20, true); // version needed
        w.setUint16(8, FLAG_UTF8, true);
        w.setUint16(10, METHOD_STORE, true);
        w.setUint16(12, time, true);
        w.setUint16(14, date, true);
        w.setUint32(16, crcs[i]!, true);
        w.setUint32(20, size, true);
        w.setUint32(24, size, true);
        w.setUint16(28, names[i]!.length, true);
        w.setUint16(30, 0, true); // extra length
        w.setUint16(32, 0, true); // comment length
        w.setUint16(34, 0, true); // disk number start
        w.setUint16(36, 0, true); // internal attributes
        w.setUint32(38, 0, true); // external attributes
        w.setUint32(42, offsets[i]!, true);
      }),
      names[i]!,
    );
  }

  parts.push(
    record(22, (w) => {
      w.setUint32(0, END_SIG, true);
      w.setUint16(4, 0, true); // this disk
      w.setUint16(6, 0, true); // disk with the central directory
      w.setUint16(8, entries.length, true);
      w.setUint16(10, entries.length, true);
      w.setUint32(12, centralSize, true);
      w.setUint32(16, localTotal, true);
      w.setUint16(20, 0, true); // no archive comment
    }),
  );

  return new Blob(parts, { type: 'application/zip' });
}
